// VRG-734 — Content analytics: the arithmetic behind the table. Pure, so the guard can pin it.
import type { WebsiteContentDay, WebsiteContentRow } from 'services'

import type { Tone } from '../../InternalAnalyticsPage/components/shared'

/**
 * Below this many views a rate shows without colour: 40% completion from 5 plays is noise
 * (spec §4, "Read the numbers with care").
 */
export const COLOUR_MIN_VIEWS = 100

/** A rate with its denominator. `share` is null when the denominator is 0: unknown, not 0%. */
export interface Rate {
  n: number
  hits: number
  share: number | null
}

export const rate = (hits: number, n: number): Rate => ({ n, hits, share: n > 0 ? hits / n : null })

/** Read-through: of the scroll-measured views, the share that reached 90% of the page. */
export const readThrough = (r: WebsiteContentRow): Rate => rate(r.reached.p90, r.scrolledViews)

export const median = (values: readonly number[]): number | null => {
  if (!values.length) return null
  const s = [...values].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
}

/**
 * Colour is RELATIVE (consented visitors are a skewed sample, so absolute rates mislead): a
 * piece's rate against the median of the OTHER pieces that also cleared COLOUR_MIN_VIEWS.
 * Fewer than two such pieces → nothing to compare → neutral.
 */
export const relativeTone = (value: number | null, views: number, peers: readonly number[]): Tone => {
  if (value === null || views < COLOUR_MIN_VIEWS || peers.length < 2) return 'neutral'
  const m = median(peers)
  if (m === null || value === m) return 'neutral'
  return value > m ? 'positive' : 'caution'
}

export const percent = (share: number | null): string => (share === null ? '—' : `${Math.round(share * 100)}%`)

export const duration = (seconds: number | null): string => {
  if (seconds === null) return '—'
  const s = Math.round(seconds)
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`
}

/** "/insights/x/" and "/Insights/x?utm=…" are one piece (mirrors the API's contentPath). */
export const normalisePath = (p: string): string => p.split(/[?#]/)[0].toLowerCase().replace(/\/+$/, '')

/** UTC day range ending today, `days` long. */
export const dayRange = (days: number, now = new Date()): { since: string; until: string } => {
  const until = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
  const since = new Date(until)
  since.setUTCDate(since.getUTCDate() - (days - 1))
  return { since: since.toISOString().slice(0, 10), until: until.toISOString().slice(0, 10) }
}

/**
 * VRG-765 — the SIGN-UP rate: Get started forms submitted over Get started forms opened. Its n is
 * the opens, so the cell can show "3 of 12 opens" and a reader can see the denominator rather than
 * a bare percentage. Zero opens means unknown, not 0% — `rate`'s own rule.
 */
export const signupRate = (r: WebsiteContentRow): Rate =>
  rate(r.outcomes.getStartedSubmitted, r.outcomes.getStartedOpened)

/**
 * VRG-765 — the charts' series: every UTC day in the range, oldest first, with the days PostHog
 * returned no row for filled in as ZERO.
 *
 * Filling is right here and wrong in general: a day inside the range with no `$pageview` event
 * genuinely had no views, so zero is a measurement. It is NOT a stand-in for a failed read — that
 * case is `days === null` from the API, and the page draws no chart at all for it.
 */
export const fillContentDays = (
  days: readonly WebsiteContentDay[],
  range: { since: string; until: string },
): WebsiteContentDay[] => {
  const seen = new Map(days.map((d) => [d.day, d]))
  const out: WebsiteContentDay[] = []
  const start = Date.parse(`${range.since}T00:00:00Z`)
  const end = Date.parse(`${range.until}T00:00:00Z`)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [...days]
  for (let t = start; t <= end; t += 86_400_000) {
    const day = new Date(t).toISOString().slice(0, 10)
    out.push(seen.get(day) ?? { day, views: 0, subscribed: 0, getStartedSubmitted: 0 })
  }
  return out
}

/**
 * VRG-765 — which block a piece belongs in. The manifest's `kind` is the authority: `video` is a
 * VERAfied interview, everything else reads as an article or an explainer. A path PostHog saw that
 * the manifest does not list has no kind, so it stays with the articles rather than being guessed
 * into the interviews block.
 */
export const isInterviewKind = (kind: string): boolean => kind === 'video'
